import { Button, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text, Title2, Title3, Tooltip } from "@fluentui/react-components";
import { EyeOffRegular, EyeRegular, SearchRegular } from "@fluentui/react-icons";
import { ReactNode, useContext, useEffect, useState } from "react";
import { TaskListItem } from "../models/task";
import { getTasks } from "../services/taskService";
import { getUsersByDepartment, getUsersByModule } from "../services/usersService";
import { set } from "react-hook-form";
import { useAppContext } from "../AppContext";
import { Module } from "../models/module";
import Paginator from "../utils/Paginator";
import EditTaskDialog from "./dialogs/EditTaskDialog";
import { User } from "../models/users";

const TenderChanges = () => {
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<TaskListItem | undefined>();

  const {user} = useAppContext();

  useEffect(() => {
    const fetchUsers = async () => {
      try{
        const response = await getUsersByModule(1);
        setUsers(response.map((u: any) => u.user));
      }
      catch(error){
        console.error("Error fetching users:", error);
      }
    }

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getTasks(showAll, 50, pageIndex);
        setTasks(response.tasks.filter((t: TaskListItem) => t.taskTypeID !== 5));
        setTotalCount(response.pageTotal);
        console.log("Fetched tasks:", response); // Log the response instead
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pageIndex, showAll]);

  const getParentModuleName = (moduleID: number): string => {
    const modules = user.modules;
    const module = modules.find((mod: Module) => mod.moduleID === moduleID);

    if (!module) return "Okänd ramavtal";

    const parentModuleID = module.modulePath.split("_")[2];
    const parentModule = modules.find((mod: Module) => mod.moduleID === +parentModuleID);

    return parentModule ? parentModule.moduleName : "Okänd ramavtal";
  }

  const columns: {label: string, render: (item: TaskListItem) => ReactNode, width?: string}[] = [
    {
      label: "Verksamhetens namn",
      render: (item: TaskListItem) => item.taskName,
      width: "15%"
    },
    {
      label: "Ramavtal",
      render: (item: TaskListItem) => getParentModuleName(item.taskModuleID),
      width: "15%"
    },
    {
      label: "Ändring",
      render: (item: TaskListItem) => item.taskDescription.substring(0, 50) + (item.taskDescription.length > 50 ? "..." : ""),
      width: "25%"
    },
    {
      label: "Ärendenr",
      render: (item: TaskListItem) => item.taskID
    },
    {
      label: "Status",
      render: (item: TaskListItem) => {
        if (item.isTaskApproved && item.isTaskCompleted) {
          return 'Accepterad';
        }
        else if (item.isTaskApproved) {
          return 'Godkänt';
        }
        else if (item.isTaskApproved === false) {
          return 'Nekad';
        }
        else if (item.isTaskApproved === null) {
          return 'Väntar på svar';
        }
      }
    },
    {
      label: "Datum",
      render: (item: TaskListItem) => new Date(item.taskCreated).toLocaleDateString('sv-SE')
    },
    {
      label: "Ansv.",
      render: (item: TaskListItem) => {
        if (users) {
          const user = users.find(u => u.userID === item.taskOwner);
          if (user) {
            const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
            return (
              <Tooltip content={`${user.firstName} ${user.lastName}`} relationship="label" withArrow>
                <div
                  className="inline-flex w-[32px] h-[32px] rounded-full text-[12px] font-semibold text-white items-center justify-center ring-2 ring-gray-200 shadow-sm cursor-default"
                  style={{ backgroundColor: user.color || '#f05a22' }}
                >
                  {initials}
                </div>
              </Tooltip>
            );
          }
        }
        else {
          return '';
        }
      }
    },
    {
      label: "Kommentar",
      render: (item: TaskListItem) => item.taskComment
    },
  ];

  const handleTaskUpdated = (updatedTask: TaskListItem) => {
    console.log("Task updated:", updatedTask);
    if(updatedTask.isTaskApproved){
      setTasks(prevTasks => prevTasks.filter(task => task.taskID !== updatedTask.taskID));
    }
    else{
      setTasks(prevTasks => prevTasks.map(task => task.taskID === updatedTask.taskID ? updatedTask : task));
    }
  }

  const handleRowClick = (item: TaskListItem) => {
    setSelectedTask(item);
    setShowEditDialog(true);
  }

  return(
    <>
      <div className="bg-[#ebebeb]">
        <div className="h-[130px] bg-gradient-to-r rounded from-orange-700 to-yellow-500">
  
        </div>
        <div className="bg-white flex flex-col gap-5 m-9 pt-9 p-5 rounded -mt-20">
          <Title2>Ändringsbegäran av uppgifter</Title2>
          <div className="flex gap-2">
            <Button disabled={loading} onClick={() => setShowAll(!showAll)} icon={showAll ? <EyeOffRegular /> : <EyeRegular />}>{showAll ? "Dölj avklarade" : "Visa avklarade"}</Button>
            <Button disabled={loading} icon={<SearchRegular/>}>Sök</Button>
          </div>
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Spinner size="large" label="Laddar..." />
            </div>
          ) : (
            <Table aria-label="table">
              <TableHeader>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableHeaderCell 
                      key={index}
                      style={column.width ? { width: column.width } : undefined}
                    >
                      <Text weight="semibold">
                        {column.label}
                      </Text>
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((item, rowIndex) => (
                  <TableRow className="cursor-pointer" onClick={() => handleRowClick(item)} key={rowIndex}>
                    {columns.map((column, index) => (
                      <TableCell 
                        key={index}
                        style={column.width ? { width: column.width } : undefined}
                      >
                        {column.render(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        <Paginator currentPage={pageIndex} onPageChange={(page: number) => setPageIndex(page)} totalCount={totalCount} />
        </div>
      </div>
      {selectedTask && <EditTaskDialog open={showEditDialog} onClose={() => {setShowEditDialog(false); setSelectedTask(undefined);}} task={selectedTask} users={users} onTaskUpdated={handleTaskUpdated} />}
    </>
  )
}

export default TenderChanges;